import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Demo Context - provides demo state and data to all components
const DemoContext = createContext(null);

// Helper to check if we're in demo mode from URL or sessionStorage
const checkDemoMode = () => {
  if (typeof window === 'undefined') return false;
  const urlParams = new URLSearchParams(window.location.search);
  const hasDemoParam = urlParams.get('demo') === 'true';
  const hasActiveDemo = !!sessionStorage.getItem('activeDemo');
  // Demo mode is active if:
  // 1. URL has ?demo=true AND session exists, OR
  // 2. Session exists and hasn't expired (trust session over URL for navigation)
  if (hasActiveDemo) {
    try {
      const session = JSON.parse(sessionStorage.getItem('activeDemo'));
      const expiresAt = session.expiresAt ? new Date(session.expiresAt) : null;
      if (expiresAt && expiresAt > new Date()) {
        return true; // Valid session exists
      }
    } catch (e) {
      // Invalid session, require URL param
    }
  }
  return hasDemoParam && hasActiveDemo;
};

// Helper to get demo session data
const getDemoSession = () => {
  if (typeof window === 'undefined') return null;
  try {
    const activeDemo = sessionStorage.getItem('activeDemo');
    if (activeDemo) {
      return JSON.parse(activeDemo);
    }
  } catch (e) {
    console.error('Failed to parse demo session:', e);
  }
  return null;
};

export const DemoProvider = ({ children }) => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [demoSession, setDemoSession] = useState(null);
  const [demoData, setDemoData] = useState(null);
  const [demoUser, setDemoUser] = useState(null);
  const [template, setTemplate] = useState(null);

  // Initialize demo state
  useEffect(() => {
    const isDemo = checkDemoMode();
    setIsDemoMode(isDemo);
    
    if (isDemo) {
      const session = getDemoSession();
      if (session) {
        setDemoSession(session);
        setDemoData(session.demoData || {});
        setDemoUser(session.demoUser || null);
        setTemplate(session.template || null);
      }
    }
  }, []);

  // Listen for URL changes (both popstate and pushState from React Router)
  useEffect(() => {
    const handleLocationChange = () => {
      const isDemo = checkDemoMode();
      setIsDemoMode(isDemo);
      
      if (isDemo) {
        const session = getDemoSession();
        if (session) {
          setDemoSession(session);
          setDemoData(session.demoData || {});
          setDemoUser(session.demoUser || null);
          setTemplate(session.template || null);
        }
      }
    };

    // Listen for browser back/forward
    window.addEventListener('popstate', handleLocationChange);
    
    // Intercept pushState and replaceState for React Router navigation
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      handleLocationChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      handleLocationChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  // Get specific demo data by type
  const getDemoDataByType = useCallback((dataType) => {
    if (!isDemoMode || !demoData) return null;
    return demoData[dataType] || null;
  }, [isDemoMode, demoData]);

  // Utility to wrap fetch calls - returns demo data if in demo mode
  const fetchWithDemo = useCallback(async (dataType, fetchFn) => {
    if (isDemoMode && demoData && demoData[dataType]) {
      // Return demo data instead of making API call
      return demoData[dataType];
    }
    // Otherwise, execute the actual fetch
    return await fetchFn();
  }, [isDemoMode, demoData]);

  // Show "View Only" warning when user tries to edit
  const [showViewOnlyWarning, setShowViewOnlyWarning] = useState(false);
  
  const triggerViewOnlyWarning = useCallback(() => {
    if (isDemoMode) {
      setShowViewOnlyWarning(true);
      setTimeout(() => setShowViewOnlyWarning(false), 3000);
      return true; // Indicate action was blocked
    }
    return false; // Action was not blocked
  }, [isDemoMode]);

  // Block action if in demo mode (for mutations)
  const blockIfDemo = useCallback((actionCallback) => {
    if (isDemoMode) {
      triggerViewOnlyWarning();
      return false;
    }
    if (actionCallback) actionCallback();
    return true;
  }, [isDemoMode, triggerViewOnlyWarning]);

  const value = {
    isDemoMode,
    demoSession,
    demoData,
    demoUser,
    template,
    getDemoDataByType,
    fetchWithDemo,
    showViewOnlyWarning,
    setShowViewOnlyWarning,
    triggerViewOnlyWarning,
    blockIfDemo,
    sessionId: demoSession?.sessionId,
    expiresAt: demoSession?.expiresAt,
    allowedPages: demoSession?.allowedPages || []
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

// Hook to use demo context
export const useDemoMode = () => {
  const context = useContext(DemoContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      isDemoMode: checkDemoMode(),
      demoData: getDemoSession()?.demoData || null,
      demoUser: getDemoSession()?.demoUser || null,
      template: getDemoSession()?.template || null,
      getDemoDataByType: () => null,
      fetchWithDemo: async (_, fetchFn) => await fetchFn(),
      showViewOnlyWarning: false,
      setShowViewOnlyWarning: () => {},
      triggerViewOnlyWarning: () => false,
      blockIfDemo: (cb) => { if (cb) cb(); return true; },
      sessionId: null,
      expiresAt: null,
      allowedPages: []
    };
  }
  return context;
};

// HOC to wrap components with demo awareness
export const withDemoMode = (WrappedComponent) => {
  return function DemoAwareComponent(props) {
    const demoContext = useDemoMode();
    return <WrappedComponent {...props} demoContext={demoContext} />;
  };
};

// View Only Warning Toast Component
export const ViewOnlyToast = () => {
  const { showViewOnlyWarning, setShowViewOnlyWarning, template } = useDemoMode();
  
  if (!showViewOnlyWarning) return null;
  
  return (
    <div 
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-4 duration-300"
      onClick={() => setShowViewOnlyWarning(false)}
    >
      <div 
        className="px-6 py-3 rounded-xl shadow-2xl flex items-center gap-3 cursor-pointer"
        style={{ 
          backgroundColor: template?.color || '#EAB308',
          color: 'white'
        }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
        </svg>
        <span className="font-medium">Demo Mode: View Only</span>
        <span className="text-white/80 text-sm">Sign up to edit!</span>
      </div>
    </div>
  );
};

export default DemoContext;
